/**
 * Question Display Component
 * Shows the current question and answer options for team players on mobile
 * Team captain can submit answers, other team members can only view
 */

import React, { useMemo, useRef, useEffect, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  Dimensions,
  Animated,
  Easing,
  ActivityIndicator,
} from "react-native";
import { Image } from "expo-image";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useThemeColors } from "@/providers/ThemeProvider";
import { QuizQuestion, QuestionResult } from "../types";

const { width: SCREEN_WIDTH } = Dimensions.get("window");

/**
 * Option colors matching Kahoot style
 */
const OPTION_COLORS = ["#EF4444", "#3B82F6", "#F59E0B", "#10B981"];
const OPTION_SHAPES = ["▲", "◆", "●", "■"];

interface QuestionDisplayProps {
  /** Current question */
  question: QuizQuestion;
  /** Question number (1-indexed) */
  questionNumber: number;
  /** Total questions in quiz */
  totalQuestions: number;
  /** Time remaining in seconds */
  timeRemaining: number;
  /** Whether the user is team captain (can answer) */
  isTeamCaptain: boolean;
  /** Selected answer index (if any) */
  selectedAnswer: number | null;
  /** Whether answer has been submitted */
  isAnswerSubmitted: boolean;
  /** Question result (after answer reveal) */
  result: QuestionResult | null;
  /** Is loading (submitting answer) */
  isLoading?: boolean;
  /** Handler for selecting an answer */
  onSelectAnswer: (optionIndex: number) => void;
  /** Quiz color for theming */
  quizColor?: string;
}

/**
 * Question Display for mobile players
 */
export const QuestionDisplay: React.FC<QuestionDisplayProps> = ({
  question,
  questionNumber,
  totalQuestions,
  timeRemaining,
  isTeamCaptain,
  selectedAnswer,
  isAnswerSubmitted,
  result,
  isLoading = false,
  onSelectAnswer,
  quizColor = "#8B5CF6",
}) => {
  const colors = useThemeColors();
  
  // Animations - using useMemo to recreate when question changes
  const timerAnim = useRef(new Animated.Value(1)).current;
  const shakeAnim = useRef(new Animated.Value(0)).current;
  const correctAnim = useRef(new Animated.Value(0)).current;
  
  // Create stable animation values for options - recreate when question.id changes
  const optionAnims = useMemo(() => {
    return question.options.map(() => new Animated.Value(1));
  }, [question.id]);
  
  // Animate timer urgency (shake when low)
  useEffect(() => {
    if (timeRemaining <= 5 && timeRemaining > 0 && !isAnswerSubmitted) {
      Animated.sequence([
        Animated.timing(shakeAnim, {
          toValue: 10,
          duration: 50,
          useNativeDriver: true,
        }),
        Animated.timing(shakeAnim, {
          toValue: -10,
          duration: 50,
          useNativeDriver: true,
        }),
        Animated.timing(shakeAnim, {
          toValue: 0,
          duration: 50,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [timeRemaining, isAnswerSubmitted, shakeAnim]);
  
  // Animate correct answer reveal
  useEffect(() => {
    if (result) {
      Animated.spring(correctAnim, {
        toValue: 1,
        tension: 50,
        friction: 5,
        useNativeDriver: true,
      }).start();
    } else {
      correctAnim.setValue(0);
    }
  }, [result, correctAnim]);
  
  /**
   * Handle option press
   */
  const handleOptionPress = useCallback((index: number) => {
    if (!isTeamCaptain || isAnswerSubmitted || isLoading || timeRemaining <= 0) {
      return;
    }
    
    // Pulse animation - with null check
    const anim = optionAnims[index];
    if (anim) {
      Animated.sequence([
        Animated.timing(anim, {
          toValue: 0.9,
          duration: 100,
          useNativeDriver: true,
        }),
        Animated.spring(anim, {
          toValue: 1,
          tension: 200,
          friction: 10,
          useNativeDriver: true,
        }),
      ]).start();
    }
    
    onSelectAnswer(index);
  }, [isTeamCaptain, isAnswerSubmitted, isLoading, timeRemaining, optionAnims, onSelectAnswer]);
  
  /**
   * Get timer color based on remaining time
   */
  const timerColor = useMemo(() => {
    if (timeRemaining <= 3) return "#EF4444";
    if (timeRemaining <= 5) return "#F59E0B";
    return "#10B981";
  }, [timeRemaining]);
  
  /**
   * Determine if option is the correct answer
   */
  const isCorrectOption = useCallback((index: number) => {
    return result && index === result.correct_option_index;
  }, [result]);
  
  /**
   * Get option style based on state
   */
  const getOptionStyle = useCallback((index: number) => {
    const isSelected = selectedAnswer === index;
    const isCorrect = isCorrectOption(index);
    
    if (result) {
      // After result reveal
      if (isCorrect) {
        return {
          backgroundColor: "#10B981",
          borderColor: "#10B981",
          opacity: 1,
        };
      } else if (isSelected && !isCorrect) {
        return {
          backgroundColor: "#EF4444",
          borderColor: "#EF4444",
          opacity: 1,
        };
      } else {
        return {
          backgroundColor: OPTION_COLORS[index] + "40",
          borderColor: OPTION_COLORS[index] + "60",
          opacity: 0.6,
        };
      }
    }
    
    if (isSelected) {
      return {
        backgroundColor: OPTION_COLORS[index],
        borderColor: "#FFFFFF",
        opacity: 1,
      };
    }
    
    return {
      backgroundColor: OPTION_COLORS[index],
      borderColor: "transparent",
      opacity: isTeamCaptain && !isAnswerSubmitted && timeRemaining > 0 ? 1 : 0.7,
    };
  }, [selectedAnswer, result, isCorrectOption, isTeamCaptain, isAnswerSubmitted, timeRemaining]);
  
  return (
    <View className="flex-1">
      {/* Header with timer */}
      <Animated.View
        style={[
          {
            transform: [{ translateX: shakeAnim }],
          },
        ]}
        className="px-4 py-3"
      >
        <View className="flex-row items-center justify-between mb-2">
          <View 
            className="px-3 py-1 rounded-full"
            style={{ backgroundColor: quizColor + "30" }}
          >
            <Text className="font-bold" style={{ color: quizColor }}>
              Vraag {questionNumber}/{totalQuestions}
            </Text>
          </View>
          
          {/* Timer */}
          <View 
            className="flex-row items-center px-4 py-2 rounded-full"
            style={{ backgroundColor: timerColor + "20" }}
          >
            <Ionicons name="time" size={18} color={timerColor} />
            <Text 
              className="ml-2 font-bold text-lg"
              style={{ color: timerColor }}
            >
              {timeRemaining}s
            </Text>
          </View>
        </View>
        
        {/* Timer bar */}
        <View 
          className="h-1 rounded-full overflow-hidden"
          style={{ backgroundColor: colors.border }}
        >
          <View
            className="h-full rounded-full"
            style={{
              width: `${Math.max(0, (timeRemaining / 30) * 100)}%`,
              backgroundColor: timerColor,
            }}
          />
        </View>
      </Animated.View>
      
      {/* Question */}
      <View className="px-4 py-6">
        {question.image_url && (
          <View className="items-center mb-4">
            <Image
              source={{ uri: question.image_url }}
              style={{
                width: SCREEN_WIDTH - 32,
                height: (SCREEN_WIDTH - 32) * 0.5,
                borderRadius: 16,
              }}
              contentFit="contain"
              cachePolicy="memory-disk"
            />
          </View>
        )}
        <Text 
          className="text-xl font-bold text-center"
          style={{ color: colors.text }}
        >
          {question.question}
        </Text>
        
        {/* Captain indicator */}
        {!isTeamCaptain && !result && (
          <View 
            className="mt-3 px-4 py-2 rounded-full self-center"
            style={{ backgroundColor: colors.surface }}
          >
            <Text className="text-center text-sm" style={{ color: colors.textSecondary }}>
              👀 Wacht tot je teamcaptain antwoordt
            </Text>
          </View>
        )}
      </View>
      
      {/* Answer Options */}
      <View className="flex-1 px-4 pb-4">
        <View className="flex-row flex-wrap gap-3">
          {question.options.map((option, index) => {
            const style = getOptionStyle(index);
            const isSelected = selectedAnswer === index;
            const isCorrect = isCorrectOption(index);
            const optionAnim = optionAnims[index];
            
            return (
              <Animated.View
                key={`${question.id}-${index}`}
                style={[
                  {
                    width: (SCREEN_WIDTH - 44) / 2,
                    transform: [
                      { scale: optionAnim || 1 },
                      isCorrect && result
                        ? {
                            scale: correctAnim.interpolate({
                              inputRange: [0, 0.5, 1],
                              outputRange: [1, 1.1, 1.05],
                            }),
                          }
                        : { scale: 1 },
                    ],
                    opacity: style.opacity,
                  },
                ]}
              >
                <TouchableOpacity
                  onPress={() => handleOptionPress(index)}
                  disabled={!isTeamCaptain || isAnswerSubmitted || isLoading || timeRemaining <= 0}
                  activeOpacity={0.8}
                  className="rounded-2xl overflow-hidden"
                  style={{
                    backgroundColor: style.backgroundColor,
                    borderWidth: isSelected ? 3 : 0,
                    borderColor: style.borderColor,
                  }}
                >
                  <View className="p-4 min-h-[100px] items-center justify-center">
                    {/* Shape icon */}
                    <Text className="text-3xl mb-2 opacity-80">
                      {OPTION_SHAPES[index]}
                    </Text>
                    
                    {/* Option text */}
                    <Text 
                      className="text-center font-semibold text-base"
                      style={{ color: "#FFFFFF" }}
                    >
                      {option.text}
                    </Text>
                    
                    {/* Result indicators */}
                    {result && isCorrect && (
                      <View className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white items-center justify-center">
                        <Ionicons name="checkmark" size={20} color="#10B981" />
                      </View>
                    )}
                    
                    {result && isSelected && !isCorrect && (
                      <View className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white items-center justify-center">
                        <Ionicons name="close" size={20} color="#EF4444" />
                      </View>
                    )}
                    
                    {/* Answer count in result */}
                    {result && (
                      <View 
                        className="mt-2 px-3 py-1 rounded-full"
                        style={{ backgroundColor: "rgba(0,0,0,0.2)" }}
                      >
                        <Text className="text-white text-xs font-medium">
                          {result.answer_counts[index] || 0} teams
                        </Text>
                      </View>
                    )}
                  </View>
                </TouchableOpacity>
              </Animated.View>
            );
          })}
        </View>
      </View>
      
      {/* Status bar at bottom */}
      <View 
        className="px-4 py-3 border-t"
        style={{ 
          backgroundColor: colors.surface,
          borderTopColor: colors.border,
        }}
      >
        {isLoading ? (
          <View className="flex-row items-center justify-center">
            <ActivityIndicator size="small" color={colors.primary} />
            <Text className="ml-2" style={{ color: colors.textSecondary }}>
              Antwoord verzenden...
            </Text>
          </View>
        ) : isAnswerSubmitted && selectedAnswer !== null ? (
          <View className="flex-row items-center justify-center">
            <Ionicons name="checkmark-circle" size={24} color="#10B981" />
            <Text className="ml-2 font-medium" style={{ color: "#10B981" }}>
              Antwoord verzonden! Wachten op resultaat...
            </Text>
          </View>
        ) : result ? (
          <View className="flex-row items-center justify-center">
            {selectedAnswer === result.correct_option_index ? (
              <>
                <Ionicons name="trophy" size={24} color="#F59E0B" />
                <Text className="ml-2 font-bold" style={{ color: "#F59E0B" }}>
                  Correct! Punten verdiend!
                </Text>
              </>
            ) : (
              <>
                <Ionicons name="close-circle" size={24} color="#EF4444" />
                <Text className="ml-2 font-medium" style={{ color: "#EF4444" }}>
                  Helaas, dat was niet juist
                </Text>
              </>
            )}
          </View>
        ) : timeRemaining <= 0 ? (
          <View className="flex-row items-center justify-center">
            <Ionicons name="time" size={24} color="#EF4444" />
            <Text className="ml-2 font-medium" style={{ color: "#EF4444" }}>
              Tijd is om!
            </Text>
          </View>
        ) : isTeamCaptain ? (
          <Text className="text-center" style={{ color: colors.textSecondary }}>
            Tik op een antwoord om te selecteren
          </Text>
        ) : (
          <Text className="text-center" style={{ color: colors.textSecondary }}>
            Alleen de teamcaptain kan antwoorden
          </Text>
        )}
      </View>
    </View>
  );
};

export default QuestionDisplay;

